/**
 * @component FaqPage
 * @description 고객 문의 전 자주 묻는 질문을 빠르게 확인할 수 있도록 FAQ 아코디언 목록을 제공하는 페이지입니다.
 *              /cs/faq_edit 경로로 진입하면 관리자 모드에서 FAQ를 추가/삭제할 수 있습니다.
 * @usage App 라우팅에서 `/cs/faq.html` 경로일 때 렌더링됩니다.
 */

import { useState, useEffect, useRef } from 'react';
import PageHero from '../../components/common/PageHero';
import SectionHeading from '../../components/common/SectionHeading';
import PasswordVerifyModal from '../../components/contact/PasswordVerifyModal';
import { pageMeta } from '../../constants/navigation';
import { useFaqAccordion } from '../../hooks/useFaqAccordion';

function makeEmptyRow() {
  return { id: Date.now() + Math.random(), question: '', answer: '' };
}

export default function FaqPage({ onNavigate, adminTrigger }) {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // 관리자 인증 상태
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState('');
  const [adminKeyError, setAdminKeyError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [adminMode, setAdminMode] = useState(false);

  // 편집 상태
  const [deletedIndexes, setDeletedIndexes] = useState(new Set());
  const [pendingRows, setPendingRows] = useState([makeEmptyRow()]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const { expandedFaq, toggleFaq } = useFaqAccordion();
  const addSectionRef = useRef(null);

  // [비즈니스] /cs/faq_edit 경로 진입 시 관리자 인증 모달을 자동으로 엽니다.
  useEffect(() => {
    if (adminTrigger) setShowAdminModal(true);
  }, [adminTrigger]);

  // [기능] 백엔드 API에서 FAQ 목록을 가져옵니다.
  useEffect(() => {
    fetch('/api/faq')
      .then((r) => r.json())
      .then((data) => { setFaqItems(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // [기능] 관리자 키 인증 요청을 처리합니다.
  const handleVerifyAdmin = async () => {
    setIsVerifying(true);
    setAdminKeyError('');
    try {
      const res = await fetch('/api/faq/verify', {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKeyInput },
      });
      if (res.ok) {
        setAdminKey(adminKeyInput);
        setAdminMode(true);
        setShowAdminModal(false);
        setAdminKeyInput('');
        // [비즈니스] 인증 성공 후 URL의 관리자 진입 경로를 정리합니다.
        if (adminTrigger) onNavigate('/cs/faq.html');
        setTimeout(() => addSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      } else {
        setAdminKeyError('관리자 키가 올바르지 않습니다.');
      }
    } catch {
      setAdminKeyError('서버 오류가 발생했습니다.');
    } finally {
      setIsVerifying(false);
    }
  };

  // [기능] 기존 FAQ 항목을 삭제 대상으로 표시하거나 표시를 해제합니다.
  const toggleDelete = (index) => {
    setDeletedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // [기능] 마지막 pending row의 "+" 클릭 시 새 행을 추가합니다.
  const handleAddRow = () => {
    setPendingRows((prev) => [...prev, makeEmptyRow()]);
  };

  // [기능] pending row의 "-" 클릭 시 해당 행을 제거합니다.
  const handleRemovePendingRow = (id) => {
    setPendingRows((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length > 0 ? next : [makeEmptyRow()];
    });
  };

  const updatePendingRow = (id, field, value) => {
    setPendingRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };

  // [기능] 삭제/추가 내용을 합산해 전체 저장합니다.
  const handleSave = async () => {
    setSaveError('');
    setIsSaving(true);
    try {
      // [비즈니스] 삭제 표시되지 않은 기존 항목 유지
      const remaining = faqItems.filter((_, i) => !deletedIndexes.has(i));

      // [비즈니스] 질문이 있는 pending rows만 추가
      const newEntries = pendingRows
        .filter((r) => String(r.question).trim() !== '')
        .map((r) => ({
          question: String(r.question).trim(),
          answer: String(r.answer).trim(),
        }));

      const combined = [...remaining, ...newEntries];

      const res = await fetch('/api/faq', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify(combined),
      });

      if (res.ok) {
        const saved = await res.json();
        setFaqItems(saved);
        setDeletedIndexes(new Set());
        setPendingRows([makeEmptyRow()]);
      } else {
        const msg = await res.text().catch(() => '');
        setSaveError(`저장 실패 (${res.status})${msg ? ': ' + msg : ''}`);
      }
    } catch {
      setSaveError('네트워크 오류가 발생했습니다. 서버가 실행 중인지 확인해 주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = deletedIndexes.size > 0 || pendingRows.some((r) => String(r.question).trim() !== '');

  return (
    <>
      <PageHero title={pageMeta.faq.title} trail={pageMeta.faq.trail} className="cs-page-hero" />
      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="FAQ"
            title="자주 묻는 질문을 먼저 확인해보세요"
            description="도입 방식, 지원 범위, 상담 절차 등 자주 문의하시는 내용을 정리했습니다."
            className="fade-in"
          />

          {adminMode && (
            <div className="faq-admin-bar">
              <span className="faq-admin-badge">관리자 모드</span>
              <span className="faq-admin-hint">삭제할 항목의 [-] 버튼을 클릭하세요</span>
              <div className="faq-admin-bar-actions">
                {saveError && <span className="faq-admin-error">{saveError}</span>}
                <button
                  type="button"
                  className="faq-admin-save-btn"
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                >
                  {isSaving ? '저장 중...' : '저장'}
                </button>
                <button
                  type="button"
                  className="faq-admin-exit-btn"
                  onClick={() => { setAdminMode(false); setDeletedIndexes(new Set()); setPendingRows([makeEmptyRow()]); setSaveError(''); }}
                >
                  모드 종료
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <p style={{ color: 'rgba(32,35,50,0.5)', fontSize: '0.95rem' }}>로딩 중...</p>
          ) : (
            <div className="faq-list">
              {faqItems.map((item, index) => {
                const isOpen = expandedFaq === item.question;
                const isDeleted = deletedIndexes.has(index);

                return (
                  <article
                    key={index}
                    className={`faq-card card-panel fade-in ${isOpen && !isDeleted ? 'is-open' : ''} ${isDeleted ? 'faq-entry-deleted' : ''}`}
                  >
                    <div className="faq-trigger-row">
                      {/* [이벤트 처리]: 질문 버튼 클릭 시 해당 항목의 펼침/접힘 상태를 전환합니다. */}
                      <button
                        type="button"
                        className="faq-trigger"
                        onClick={() => !isDeleted && toggleFaq(item.question)}
                        aria-expanded={isOpen && !isDeleted}
                        style={{ flex: 1 }}
                      >
                        <span>{item.question}</span>
                        <strong>{isOpen && !isDeleted ? '−' : '+'}</strong>
                      </button>
                      {adminMode && (
                        <button
                          type="button"
                          className={`faq-admin-row-btn ${isDeleted ? 'is-restore' : 'is-delete'}`}
                          onClick={() => toggleDelete(index)}
                          title={isDeleted ? '삭제 취소' : '삭제'}
                        >
                          {isDeleted ? '+' : '-'}
                        </button>
                      )}
                    </div>
                    <div className={`faq-content ${isOpen && !isDeleted ? 'is-open' : ''}`}>
                      <p>{item.answer}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* 관리자 모드: 새 FAQ 추가 섹션 */}
          {adminMode && (
            <div className="faq-admin-add-section" ref={addSectionRef}>
              <p className="faq-admin-add-title">새 FAQ 추가</p>
              <div className="faq-admin-add-list">
                {pendingRows.map((row, idx) => {
                  const isLast = idx === pendingRows.length - 1;
                  return (
                    <div key={row.id} className="faq-admin-add-row">
                      <div className="faq-admin-add-fields">
                        <input
                          type="text"
                          className="faq-admin-input faq-admin-input-question"
                          value={row.question}
                          placeholder="질문을 입력하세요"
                          onChange={(e) => updatePendingRow(row.id, 'question', e.target.value)}
                        />
                        <textarea
                          className="faq-admin-input faq-admin-input-answer"
                          value={row.answer}
                          placeholder="답변을 입력하세요"
                          rows={3}
                          onChange={(e) => updatePendingRow(row.id, 'answer', e.target.value)}
                        />
                      </div>
                      {isLast ? (
                        <button
                          type="button"
                          className="faq-admin-row-btn is-add"
                          onClick={handleAddRow}
                          title="행 추가"
                        >
                          +
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="faq-admin-row-btn is-delete"
                          onClick={() => handleRemovePendingRow(row.id)}
                          title="행 삭제"
                        >
                          -
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      <PasswordVerifyModal
        isOpen={showAdminModal}
        password={adminKeyInput}
        errorMessage={adminKeyError}
        isSubmitting={isVerifying}
        onChangePassword={(e) => setAdminKeyInput(e.target.value)}
        onConfirm={handleVerifyAdmin}
        onCancel={() => { setShowAdminModal(false); setAdminKeyInput(''); setAdminKeyError(''); }}
      />
    </>
  );
}
